import type {
  DragEvent,
  PointerEvent,
  RefObject,
  WheelEvent,
} from 'react';

type UploadModalProps = {
  data: {
    isOpen: boolean;
    previewUrl: string;
    zoom: number;
    isDragging: boolean;
    dragOffset: { x: number; y: number };
    uploadError: string;
    isSaving: boolean;
    canSave: boolean;
    hasError: boolean;
    errorMessage: string;
  };
  refs: {
    fileInputRef: RefObject<HTMLInputElement>;
    previewContainerRef: RefObject<HTMLDivElement>;
    previewImageRef: RefObject<HTMLImageElement>;
  };
  actions: {
    onClose: () => void;
    onSave: () => void;
    onChooseFile: () => void;
    onFileSelected: (file?: File) => void;
    onZoomChange: (value: number) => void;
    onZoomIn: () => void;
    onZoomOut: () => void;
    onWheel: (event: WheelEvent<HTMLDivElement>) => void;
    onPointerDown: (event: PointerEvent<HTMLDivElement>) => void;
    onImageLoad: () => void;
  };
};

const UploadModal = ({ data, refs, actions }: UploadModalProps) => {
  const {
    isOpen,
    previewUrl,
    zoom,
    isDragging,
    dragOffset,
    uploadError,
    isSaving,
    canSave,
    hasError,
    errorMessage,
  } = data;
  const { fileInputRef, previewContainerRef, previewImageRef } = refs;
  const {
    onClose,
    onSave,
    onChooseFile,
    onFileSelected,
    onZoomChange,
    onZoomIn,
    onZoomOut,
    onWheel,
    onPointerDown,
    onImageLoad,
  } = actions;
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-8">
      <div className="w-full max-w-[820px] rounded-[16px] border border-slate-200 bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              Update Profile Photo
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              Please select a jpg/jpeg, gif or png image file with size smaller than{' '}
              <span className="font-semibold text-rose-500">2MB</span> to upload
              and crop an image to be your profile picture.
            </p>
          </div>
          <button
            type="button"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:text-slate-700"
            onClick={onClose}
            aria-label="Close upload"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        <div
          className="mt-4 rounded-[12px] border border-dashed border-slate-200 bg-slate-50 p-4"
          onDragOver={(event: DragEvent<HTMLDivElement>) => event.preventDefault()}
          onDrop={(event: DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            onFileSelected(event.dataTransfer.files?.[0]);
          }}
        >
          {previewUrl ? (
            <div className="flex flex-col gap-4">
              <div
                ref={previewContainerRef}
                className="relative h-[320px] overflow-hidden rounded-[10px] bg-slate-100"
                onWheel={onWheel}
              >
                <div
                  className={`absolute inset-0 flex items-center justify-center ${
                    zoom > 1
                      ? isDragging
                        ? 'cursor-grabbing'
                        : 'cursor-grab'
                      : 'cursor-default'
                  }`}
                  onPointerDown={onPointerDown}
                >
                  <img
                    ref={previewImageRef}
                    src={previewUrl}
                    alt="Profile preview"
                    className="max-h-full max-w-full object-contain"
                    style={{
                      transform: `translate(${dragOffset.x}px, ${dragOffset.y}px) scale(${zoom})`,
                      transformOrigin: 'center',
                    }}
                    onLoad={onImageLoad}
                  />
                </div>
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="h-[300px] w-[420px] max-w-[80%] max-h-[75%] rounded-[8px] border-2 border-dashed border-white/70" />
                </div>
              </div>
              <div className="flex items-center justify-center gap-4">
                <button
                  type="button"
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 text-slate-500"
                  onClick={onZoomOut}
                  aria-label="Zoom out"
                >
                  <span className="text-lg leading-none">-</span>
                </button>
                <input
                  type="range"
                  min="1"
                  max="2"
                  step="0.01"
                  value={zoom}
                  onChange={(event) => onZoomChange(Number(event.target.value))}
                  className="w-56 accent-blue-600"
                />
                <button
                  type="button"
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 text-slate-500"
                  onClick={onZoomIn}
                  aria-label="Zoom in"
                >
                  <span className="text-lg leading-none">+</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 py-10 text-center text-sm text-slate-500">
              <p className="text-sm font-semibold text-slate-600">
                Drag and Drop a File
              </p>
              <span className="text-xs uppercase text-slate-400">Or</span>
              <button
                type="button"
                className="rounded-[10px] bg-blue-600 px-4 py-2 text-sm font-semibold text-white"
                onClick={onChooseFile}
              >
                Choose File
              </button>
              {uploadError ? (
                <p className="text-xs font-semibold text-rose-500">
                  {uploadError}
                </p>
              ) : null}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/gif"
            className="hidden"
            onChange={(event) => onFileSelected(event.target.files?.[0])}
          />
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
          <button
            type="button"
            className="rounded-[12px] border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-400 hover:text-slate-800"
            onClick={onChooseFile}
          >
            Change
          </button>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="rounded-[12px] bg-blue-600 px-5 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70"
              disabled={!canSave || isSaving}
              onClick={onSave}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              className="rounded-[12px] border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-400 hover:text-slate-800"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </div>
        {hasError ? (
          <p className="mt-3 text-xs font-semibold text-rose-500">
            {errorMessage}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default UploadModal;
